import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../../../context/authContext";
import {
  pinDigits,
  emptyPinArray,
  getStoredPinLength,
  rememberPinLength,
} from "../../../../services/api";
import { PinInput, PinLengthToggle } from "../../../../components/ui";
import AuthLogo from "../AuthLogo";
import styles from "./loginForm.module.css";

// Lista de países con códigos
const countries = [
  { code: "CO", dial: "+57", name: "Colombia", flag: "🇨🇴" },
  { code: "MX", dial: "+52", name: "México", flag: "🇲🇽" },
  { code: "US", dial: "+1", name: "Estados Unidos", flag: "🇺🇸" },
  { code: "ES", dial: "+34", name: "España", flag: "🇪🇸" },
  { code: "AR", dial: "+54", name: "Argentina", flag: "🇦🇷" },
  { code: "PE", dial: "+51", name: "Perú", flag: "🇵🇪" },
  { code: "CL", dial: "+56", name: "Chile", flag: "🇨🇱" },
  { code: "EC", dial: "+593", name: "Ecuador", flag: "🇪🇨" },
  { code: "VE", dial: "+58", name: "Venezuela", flag: "🇻🇪" },
  { code: "PA", dial: "+507", name: "Panamá", flag: "🇵🇦" },
];

export default function LoginForm({ onSwitch, onForgot, returnTo: returnToProp }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading } = useAuth();
  
  const redirectTo =
    returnToProp || location.state?.returnTo || location.state?.from || "/welcome";
  const redirectMessage = location.state?.message || null;
  
  // Estados
  const [step, setStep] = useState(1); // 1 = teléfono, 2 = PIN
  const [phone, setPhone] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(countries[0]); // Colombia por defecto
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  // Longitud del PIN: la última usada en este dispositivo (6 si no se conoce)
  const [pinLength, setPinLength] = useState(getStoredPinLength);
  const [pin, setPin] = useState(() => emptyPinArray(getStoredPinLength()));
  const [error, setError] = useState("");
  
  const dropdownRef = useRef();
  const pinInputRef = useRef(null);
  const submittingRef = useRef(false);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Formatear número de teléfono para mostrar
  const formatPhoneDisplay = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)} ${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)} ${numbers.slice(3, 6)} ${numbers.slice(6, 10)}`;
  };

  // Validar teléfono y pasar al PIN
  const handlePhoneSubmit = (e) => {
    e.preventDefault();
    setError("");
    
    if (phone.length < 10) {
      setError("Ingresa un número de teléfono válido (10 dígitos)");
      return;
    }
    
    setStep(2);
  };

  // Cambiar entre PIN de 4 y 6 dígitos: limpia lo digitado y enfoca la 1.ª casilla
  const handlePinLengthChange = (length) => {
    setPinLength(length);
    setPin(emptyPinArray(length));
    setError("");
    pinInputRef.current?.focus();
  };

  // Enviar login con la longitud elegida (4 o 6 dígitos)
  const handlePinSubmit = async (e) => {
    e?.preventDefault();
    if (submittingRef.current || isLoading) return;
    setError("");

    const pinString = pinDigits(pin);
    if (pinString.length !== pinLength) {
      setError(`Ingresa tu PIN de ${pinLength} dígitos`);
      return;
    }

    submittingRef.current = true;
    const fullPhone = `${selectedCountry.dial}${phone}`;
    const result = await login(fullPhone, pinString);

    if (result.success) {
      rememberPinLength(pinLength);
      navigate(redirectTo);
    } else {
      setError(result.error);
      setPin(emptyPinArray(pinLength));
      submittingRef.current = false;
    }
  };

  // Auto-submit al completar todas las casillas de la longitud elegida
  useEffect(() => {
    if (
      step === 2 &&
      pin.length === pinLength &&
      pin.every((digit) => digit !== "")
    ) {
      handlePinSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, step, pinLength]);

  const loginPinReady = pinDigits(pin).length === pinLength;

  // Volver al paso del teléfono
  const handleBack = () => {
    setStep(1);
    setPin(emptyPinArray(pinLength));
    setError("");
    submittingRef.current = false;
  };

  // Seleccionar país
  const selectCountry = (country) => {
    setSelectedCountry(country);
    setShowCountryDropdown(false);
  };

  return (
    <div className={styles.card}>
      {/* Logo */}
      <AuthLogo className={styles.logo} imgClassName={styles.logoImg} />

      {step === 1 ? (
        // PASO 1: TELÉFONO
        <form onSubmit={handlePhoneSubmit}>
          <h2 className={styles.title}>Bienvenido</h2>
          <p className={styles.subtitle}>Ingresa tu número para continuar</p>
          
          {/* Mensaje de redirección si viene del checkout */}
          {redirectMessage && (
            <div className={styles.redirectMessage}>
              <span>🛒</span> {redirectMessage}
            </div>
          )}
          
          <div className={styles.phoneInputContainer}>
            {/* Selector de país */}
            <div className={styles.countrySelector} ref={dropdownRef}>
              <span className={styles.countryLabel}>Country code</span>
              <button 
                type="button"
                className={styles.countrySelectorBtn}
                onClick={() => setShowCountryDropdown(!showCountryDropdown)}
              >
                <span className={styles.countryFlag}>{selectedCountry.flag}</span>
                <span className={styles.countryDial}>{selectedCountry.dial}</span>
                <span className={styles.dropdownArrow}>{showCountryDropdown ? '▴' : '▾'}</span>
              </button>
              
              {showCountryDropdown && (
                <div className={styles.countryDropdown}>
                  {countries.map((country) => (
                    <button
                      key={country.code}
                      type="button"
                      className={`${styles.countryOption} ${country.code === selectedCountry.code ? styles.countryOptionActive : ''}`}
                      onClick={() => selectCountry(country)}
                    >
                      <span>{country.flag}</span>
                      <span className={styles.countryName}>{country.name}</span>
                      <span className={styles.countryDialOption}>{country.dial}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input de teléfono */}
            <div className={styles.phoneFieldWrapper}>
              <span className={styles.phoneLabel}>Phone number</span>
              <input
                type="tel"
                value={formatPhoneDisplay(phone)}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="Ingresar número"
                className={styles.phoneField}
                autoComplete="tel"
              />
            </div>
          </div>

          {error && <p className={styles.error}>{error}</p>}

          <button 
            type="submit" 
            className={styles.primaryBtn}
            disabled={isLoading}
          >
            {isLoading ? "Cargando..." : "CONTINUAR"}
          </button>

          <p className={styles.forgotPin}>
            ¿Se te olvidó?{" "}
            <span
              onClick={onForgot}
              onKeyDown={(e) => e.key === "Enter" && onForgot?.()}
              className={styles.link}
              role="button"
              tabIndex={0}
            >
              Recuperar PIN
            </span>
          </p>

          <p className={styles.switchText}>
            ¿No tienes una cuenta?{" "}
            <span onClick={onSwitch} className={styles.link}>Regístrate</span>
          </p>
        </form>
      ) : (
        // PASO 2: PIN
        <form onSubmit={handlePinSubmit}>
          <button 
            type="button" 
            className={styles.backBtn} 
            onClick={handleBack}
          >
            ← Regresar
          </button>
          
          <h2 className={styles.title}>Ingresa tu PIN</h2>
          <p className={styles.subtitle}>
            Código de {pinLength} dígitos para<br />
            <strong>{selectedCountry.dial} {formatPhoneDisplay(phone)}</strong>
          </p>

          <PinLengthToggle
            value={pinLength}
            onChange={handlePinLengthChange}
            disabled={isLoading}
            label="¿Cuántos dígitos tiene tu PIN?"
            hint="Tu PIN puede ser de 4 o 6 dígitos"
          />
          
          <div className={styles.pinWrapper}>
            <PinInput
              ref={pinInputRef}
              length={pinLength}
              value={pin}
              onChange={setPin}
              disabled={isLoading}
              ariaLabel="PIN"
            />
          </div>

          {error && <p className={styles.error}>{error}</p>}

          <button 
            type="submit" 
            className={styles.primaryBtn}
            disabled={isLoading || !loginPinReady}
          >
            {isLoading ? "Verificando..." : "ENTRAR"}
          </button>
          
          <p className={styles.forgotPin}>
            ¿Olvidaste tu PIN?{" "}
            <span
              onClick={onForgot}
              onKeyDown={(e) => e.key === "Enter" && onForgot?.()}
              className={styles.link}
              role="button"
              tabIndex={0}
            >
              Recupéralo aquí
            </span>
          </p>
        </form>
      )}
    </div>
  );
}
